import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Paper, CircularProgress, styled } from "@mui/material";
import { ImageItem, ImageSelectorProps } from "./ImageSelector.types";
import { DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useQuery } from "@tanstack/react-query";

const ImageThumbnail = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1),
    textAlign: "center",
    cursor: "pointer",
    height: "160px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
        transform: "scale(1.05)",
        boxShadow: theme.shadows[4],
    },
    "&.selected": {
        outline: `2px solid ${theme.palette.primary.main}`,
        boxShadow: theme.shadows[6],
    },
}));

const ImageWrapper = styled(Box)({
    height: "40px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "8px",
});

const StyledImage = styled("img")({
    maxHeight: "40px",
    maxWidth: "40px",
    objectFit: "contain",
});

const ImageSelector: React.FC<ImageSelectorProps> = ({ containerUrl, onImageSelect, selectedImageUrl }) => {
    const { translateString } = useTranslation();
    const [images, setImages] = useState<ImageItem[]>([]);

    // Define the fetch function outside of useQuery for better clarity
    const fetchImages = async () => {
        const response = await fetch(`${containerUrl}?restype=container&comp=list`);

        if (!response.ok) {
            throw new Error(`Failed to fetch images: ${response.status} ${response.statusText}`);
        }

        const xmlText = await response.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, "text/xml");

        const blobElements = xmlDoc.getElementsByTagName("Blob");
        const imageItems: ImageItem[] = [];

        for (let i = 0; i < blobElements.length; i++) {
            const nameElement = blobElements[i].getElementsByTagName("Name")[0];
            if (nameElement) {
                const name = nameElement.textContent || "";
                // Only add image files
                if (/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(name)) {
                    const url = `${containerUrl}/${name}`;
                    imageItems.push({
                        name,
                        url,
                        selected: url === selectedImageUrl,
                    });
                }
            }
        }

        return imageItems;
    };

    // Use the useQuery hook
    const { data, isLoading, error } = useQuery({
        queryKey: ["images", containerUrl, selectedImageUrl],
        queryFn: fetchImages,
        enabled: !!containerUrl,
    });

    useEffect(() => {
        if (data) {
            setImages(data);
        }
    }, [data]);

    const handleImageSelect = (image: ImageItem) => {
        setImages(
            images.map((img) => ({
                ...img,
                selected: img.url === image.url,
            }))
        );
        onImageSelect(image.url);
    };

    if (isLoading && !!data) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box textAlign="center" p={2}>
                <Typography color="error">Error: {error instanceof Error ? error.message : "Failed to fetch images"}</Typography>
            </Box>
        );
    }

    if (images.length === 0) {
        return (
            <Box textAlign="center" p={2}>
                <Typography>No images found in the container.</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <DotLegalHeader headerStyle={"small"} marginTop={4}>
                {translateString("addImageToAnnualWheelActivityStandardTemplate")}
            </DotLegalHeader>
            <Grid container spacing={2}>
                {images.map((image) => (
                    <Grid item xs={6} sm={4} md={3} key={image.name}>
                        <ImageThumbnail elevation={2} onClick={() => handleImageSelect(image)} className={image.selected ? "selected" : ""}>
                            <ImageWrapper>
                                <StyledImage src={image.url} alt={image.name} loading="lazy" />
                            </ImageWrapper>
                        </ImageThumbnail>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default ImageSelector;
